import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { buildProductPath } from '../utils/productSlug';
import { collection, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { StoreContext } from '../contexts/StoreContext';
import { CartContext } from '../contexts/CartContext';
import { AuthContext } from '../contexts/AuthContext';
import { navItems, accountItems } from '../constants/navItems';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS, SPACING_ELEMENT, SPACING_COMPACT, SPACING_COMPONENT, SPACING_BUTTON, SPACING_CARD } from '../constants/layout';
import { COLORS } from '../constants/colors';
import { groupCategories, AUTH_ACTION_LABEL } from '../constants/settings';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { useNavigationBarPreferences } from '../contexts/NavigationBarPreferencesContext';
import { db } from '../firebase';

function NavigationBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { store, loading } = useContext(StoreContext);
  const { items: cartItems } = useContext(CartContext);
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const { logoURL, logoSize, navBarStyle } = useNavigationBarPreferences();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchHovered, setIsSearchHovered] = useState(false);
  const [isCartHovered, setIsCartHovered] = useState(false);
  const searchInputRef = useRef(null);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const logoFirstLoad = useRef(true);
  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice =
        window.innerWidth <= 768 ||
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
  }, [location]);

  // Helper to see if a given path is "active"
  const isActive = (path) => {
    if (path === '/') {
      return (
        location.pathname === '/' ||
        location.pathname.startsWith('/product/')
      );
    }
    return location.pathname === path;
  };

  const linkStyle = (path, overrides = {}) => {
    const active = isActive(path);
    return {
      textDecoration: 'none',
      color: active
        ? 'var(--color-navbar-pill-text)'
        : 'var(--color-navbar-text)',
      fontWeight: '600',
      padding: '6px 12px',
      borderRadius: BORDER_RADIUS,
      backgroundColor: active
        ? 'var(--color-navbar-pill-bg)'
        : 'transparent',
      transition: 'all 0.2s ease',
      display: 'block',
      ...overrides,
    };
  };

  const sidebarLinkStyle = (path) => {
    const active = isActive(path);
    return {
      textDecoration: 'none',
      color: active ? 'var(--color-navbar-pill-text)' : 'var(--color-navbar-text)',
      fontWeight: active ? '600' : 'normal',
      padding: '10px 12px',
      borderRadius: BORDER_RADIUS,
      backgroundColor: active ? 'var(--color-navbar-pill-bg)' : 'transparent',
      transition: 'all 0.2s ease',
      display: 'block',
      width: '100%',
      boxSizing: 'border-box',
    };
  };

  const SidebarDivider = () => (
    <div style={{ margin: '0 12px', borderBottom: '1px solid var(--color-border)' }} />
  );

  const sectionHeaderStyle = {
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '10px',
    paddingLeft: '12px',
    color: 'var(--color-navbar-text)',
    opacity: 0.7,
  };

  const { showContact, showFAQ, showCart, showUncategorized } = useDisplayPreferences();
  const { user } = useContext(AuthContext);
  const filteredAccountItems = (user
    ? accountItems
    : accountItems
        .filter(item => item.path !== '/bookmarks' && item.path !== '/orderhistory')
        .map(item => item.path === '/profile'
          ? { path: '/login', label: AUTH_ACTION_LABEL, icon: 'login' }
          : item
        )
  ).filter(item => item.path !== '/cart' || showCart);
  const headerNavItems = navItems.filter(item =>
    (item.path !== '/contact' || showContact) &&
    (item.path !== '/faq' || showFAQ)
  );

  // Sort categories based on groupCategories setting
  const sortedCategories = groupCategories
    ? categories
    : [...categories].sort((a, b) => {
        const titleA = (a.title || a.name || '').trim();
        const titleB = (b.title || b.name || '').trim();
        if (titleA === 'Clearance') return 1;
        if (titleB === 'Clearance') return -1;
        if (titleA === 'Product') return 1;
        if (titleB === 'Product') return -1;
        return titleA.toLowerCase().localeCompare(titleB.toLowerCase());
      });

  const hasUncategorized = useMemo(() => {
    if (!showUncategorized) return false;
    const allCategorizedIDs = new Set(categories.flatMap(c => c.itemIDs || []));
    return products.some(p => !allCategorizedIDs.has(p.id));
  }, [showUncategorized, categories, products]);

  const iconStyle = {
    fontSize: '20px',
    width: '24px',
    textAlign: 'center',
    flexShrink: 0,
  };

  const SidebarLabel = ({ icon, label }) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      {icon && <span className="material-icons" style={iconStyle}>{icon}</span>}
      {label}
    </span>
  );

  const sidebarMenuContent = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {headerNavItems.map((item, i) => (
          <React.Fragment key={item.path}>
            <Link to={item.path} className="sidebar-link" style={sidebarLinkStyle(item.path)}>
              <SidebarLabel icon={item.icon} label={item.label} />
            </Link>
            {i < headerNavItems.length - 1 && <SidebarDivider />}
          </React.Fragment>
        ))}
      </div>

      {filteredAccountItems.length > 0 && (
        <div style={{ paddingTop: '12px', display: 'flex', flexDirection: 'column' }}>
          <span style={sectionHeaderStyle}>Account</span>
          {filteredAccountItems.map((item, i) => (
            <React.Fragment key={item.path}>
              <Link to={item.path} className="sidebar-link" style={sidebarLinkStyle(item.path)}>
                <SidebarLabel icon={item.icon} label={item.label} />
              </Link>
              {i < filteredAccountItems.length - 1 && <SidebarDivider />}
            </React.Fragment>
          ))}
        </div>
      )}

      {sortedCategories.length > 0 && (
        <div style={{ paddingTop: '12px', display: 'flex', flexDirection: 'column' }}>
          <span style={sectionHeaderStyle}>Categories</span>
          {sortedCategories.map((category, i) => {
            const label = category.title || category.name || 'Category';
            return (
              <React.Fragment key={category.id}>
                <Link to={`/category/${category.id}`} className="sidebar-link" style={sidebarLinkStyle(`/category/${category.id}`)}>
                  {label}
                </Link>
                {(i < sortedCategories.length - 1 || hasUncategorized) && <SidebarDivider />}
              </React.Fragment>
            );
          })}
          {hasUncategorized && (
            <Link to="/category/more" className="sidebar-link" style={sidebarLinkStyle('/category/more')}>
              More
            </Link>
          )}
        </div>
      )}
    </>
  );

  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const [desktopSearchTerm, setDesktopSearchTerm] = useState('');
  const [isDesktopSearchFocused, setIsDesktopSearchFocused] = useState(false);
  const [isInlineSearchFocused, setIsInlineSearchFocused] = useState(false);
  const desktopSearchRef = useRef(null);

  const showModernDesktop = !isMobile && navBarStyle === 'modern';

  const searchButtonStyle = {
    border: 'none',
    backgroundColor: isSearchHovered
      ? 'var(--color-navbar-pill-bg)'
      : 'transparent',
    color: isSearchHovered
      ? 'var(--color-navbar-pill-text)'
      : 'var(--color-navbar-icon)',
    borderRadius: '9999px',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition:
      'background-color 0.2s ease, color 0.2s ease',
    flexShrink: 0,
  };

  const cartBadgeStyle = {
    position: 'absolute',
    top: '2px',
    right: '2px',
    backgroundColor: 'var(--color-danger-text)',
    color: '#fff',
    fontSize: '10px',
    fontWeight: 600,
    minWidth: '16px',
    height: '16px',
    borderRadius: '9999px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    pointerEvents: 'none',
  };

  const cartButtonStyle = {
    position: 'relative',
    border: 'none',
    backgroundColor: isCartHovered
      ? 'var(--color-navbar-pill-bg)'
      : 'transparent',
    color: isCartHovered
      ? 'var(--color-navbar-pill-text)'
      : 'var(--color-navbar-icon)',
    borderRadius: '9999px',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition:
      'background-color 0.2s ease, color 0.2s ease',
    flexShrink: 0,
  };

  const menuButtonStyle = {
    border: 'none',
    backgroundColor: isMenuHovered
      ? 'var(--color-navbar-pill-bg)'
      : 'transparent',
    color: isMenuHovered
      ? 'var(--color-navbar-pill-text)'
      : 'var(--color-navbar-icon)',
    borderRadius: '9999px',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition:
      'background-color 0.2s ease, color 0.2s ease',
    flexShrink: 0,
    padding: 0,
  };

  const iconSvgStyle = {
    width: '18px',
    height: '18px',
    display: 'block',
  };

  const trimmedDesktopSearch = desktopSearchTerm.trim();

  const desktopSearchResults = useMemo(() => {
    const term = trimmedDesktopSearch.toLowerCase();
    if (!term) return [];
    return products
      .filter(product => {
        const name = (product.productName || '').toLowerCase();
        const description = (product.description || '').toLowerCase();
        return name.includes(term) || description.includes(term);
      })
      .sort((a, b) => (a.productName || '').toLowerCase().localeCompare((b.productName || '').toLowerCase()))
      .slice(0, 8);
  }, [products, trimmedDesktopSearch]);

  const trimmedSearch = searchTerm.trim();

  const filteredProducts = useMemo(() => {
    const term = trimmedSearch.toLowerCase();
    if (!term) {
      return [];
    }

    return products
      .filter(product => {
        const name = (product.productName || '').toLowerCase();
        const description = (product.description || '').toLowerCase();
        return name.includes(term) || description.includes(term);
      })
      .sort((a, b) => {
        const nameA = (a.productName || '').toLowerCase();
        const nameB = (b.productName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [products, trimmedSearch]);

  const searchResults = filteredProducts.slice(0, 20);
  const hasQuery = trimmedSearch.length > 0;
  const showNoResults = hasQuery && searchResults.length === 0;
  const showResultsSummary = hasQuery && !showNoResults;

  const handleSearchOpen = () => {
    setIsSearchHovered(false);
    setSearchTerm('');
    setIsSearchOpen(true);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchTerm('');
    setIsSearchHovered(false);
    setIsInlineSearchFocused(false);
  };

  const handleSearchSubmit = event => {
    event.preventDefault();
    if (!trimmedSearch) return;
    navigate(`/search?q=${encodeURIComponent(trimmedSearch)}`);
    handleSearchClose();
  };

  useEffect(() => {
    if (!isSearchOpen) {
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isSearchOpen) {
      return undefined;
    }

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setIsSearchOpen(false);
        setSearchTerm('');
        setIsSearchHovered(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSearchOpen]);

  // Only show the store name once it's loaded and available
  const storeTitle = !loading && store?.name ? store.name : '';
  const brandLinkStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
    textDecoration: 'none',
  };
  const storeTitleStyle = {
    color: 'var(--color-text-primary)',
    fontSize: '20px',
    fontFamily: 'var(--font-family-base)',
    fontWeight: '600',
    letterSpacing: '-0.02em',
  };
  const clampedLogoSize = Math.min(logoSize, 72);
  const logoStyle = {
    height: `${clampedLogoSize}px`,
    width: `${clampedLogoSize}px`,
    objectFit: 'contain',
    transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
  };

  // Calculate navbar padding based on logo size
  const getNavPadding = () => {
    const paddingMap = {
      32: { mobile: 8, desktop: 10 },
      48: { mobile: 10, desktop: 12 },
      72: { mobile: 12, desktop: 16 },
    };
    return paddingMap[clampedLogoSize] || { mobile: 6, desktop: 8 };
  };

  const navPadding = getNavPadding();

  useEffect(() => {
    let isMounted = true;

    async function fetchCategories() {
      try {
        const snap = await getDocs(collection(db, 'categories'));
        if (!isMounted) return;
        const fetched = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(cat => !!(cat?.title || cat?.name))
          .sort((a, b) => {
            const titleA = (a.title || a.name || '').toLowerCase();
            const titleB = (b.title || b.name || '').toLowerCase();
            return titleA.localeCompare(titleB);
          });
        setCategories(fetched);
      } catch (error) {
        if (isMounted) {
          setCategories([]);
        }
      }
    }

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function fetchProducts() {
      try {
        const snap = await getDocs(collection(db, 'products'));
        if (!isMounted) return;
        const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProducts(fetched);
      } catch (error) {
        if (isMounted) {
          setProducts([]);
        }
      }
    }

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, []);


  // Helper function to lighten a hex color
  const lightenColor = (hex, percent) => {
    // Remove # if present
    hex = hex.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Lighten
    const newR = Math.min(255, Math.floor(r + (255 - r) * percent));
    const newG = Math.min(255, Math.floor(g + (255 - g) * percent));
    const newB = Math.min(255, Math.floor(b + (255 - b) * percent));

    // Convert back to hex
    return '#' + ((1 << 24) + (newR << 16) + (newG << 8) + newB).toString(16).slice(1);
  };

  // Helper function to darken a hex color
  const darkenColor = (hex, percent) => {
    // Remove # if present
    hex = hex.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Darken
    const newR = Math.max(0, Math.floor(r * (1 - percent)));
    const newG = Math.max(0, Math.floor(g * (1 - percent)));
    const newB = Math.max(0, Math.floor(b * (1 - percent)));

    // Convert back to hex
    return '#' + ((1 << 24) + (newR << 16) + (newG << 8) + newB).toString(16).slice(1);
  };

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Listen to brand colors preferences for dynamic theming
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'brand_colors_preferences', 'default'),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const root = document.documentElement;

          // Set base color and compute all derived colors
          if (data.brand_primary) {
            root.style.setProperty('--brand-primary', data.brand_primary);
            // Compute hover state (lightened by 15%)
            root.style.setProperty('--brand-primary-hover', lightenColor(data.brand_primary, 0.15));
            // Compute overlay (18% opacity)
            root.style.setProperty('--brand-accent-overlay', hexToRgba(data.brand_primary, 0.18));
            // Compute button color for light mode (darkened by 70%)
            const buttonColor = darkenColor(data.brand_primary, 0.70);
            root.style.setProperty('--brand-button-light', buttonColor);
            // Compute button hover (lightened from button color)
            root.style.setProperty('--brand-button-light-hover', lightenColor(buttonColor, 0.15));
          }

          // Cache to localStorage
          try {
            localStorage.setItem('brandColorsPreferences', JSON.stringify(data));
          } catch (error) {
            console.error('Error caching brand colors:', error);
          }
        }
      },
      (error) => {
        console.error('Error fetching brand colors:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Load cached brand colors on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem('brandColorsPreferences');
      if (cached) {
        const data = JSON.parse(cached);
        const root = document.documentElement;

        // Set base colors and compute all derived colors
        if (data.brand_primary) {
          root.style.setProperty('--brand-primary', data.brand_primary);
          root.style.setProperty('--brand-primary-hover', lightenColor(data.brand_primary, 0.15));
          root.style.setProperty('--brand-accent-overlay', hexToRgba(data.brand_primary, 0.18));
          const buttonColor = darkenColor(data.brand_primary, 0.70);
          root.style.setProperty('--brand-button-light', buttonColor);
          root.style.setProperty('--brand-button-light-hover', lightenColor(buttonColor, 0.15));
        }
      }
    } catch (error) {
      console.error('Error loading cached brand colors:', error);
    }
  }, []);

  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  const renderSearchResultItem = ({
    product,
    onSelect,
    divider = false,
    key,
  }) => {
    const name = product.productName || 'View product';

    return (
      <Link
        key={key || product.id}
        to={buildProductPath(product)}
        onClick={onSelect}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 12px',
          textDecoration: 'none',
          color: 'var(--color-text-primary)',
          borderBottom: divider ? '1px solid var(--color-border)' : 'none',
          transition: 'background-color 0.15s ease',
        }}
        onMouseEnter={event => {
          event.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
        }}
        onMouseLeave={event => {
          event.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            width: '14px',
            height: '14px',
            color: 'var(--color-text-secondary)',
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <span
          style={{
            fontSize: '0.9rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {name}
        </span>
      </Link>
    );
  };

  const renderShowMoreResultsLink = ({
    query,
    onSelect,
    variant = 'sheet',
    hasResults = true,
  }) => {
    const isDropdown = variant === 'dropdown';

    return (
      <Link
        to={`/search?q=${encodeURIComponent(query)}`}
        onClick={onSelect}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: isDropdown ? 0 : '4px',
          padding: '8px 12px',
          textDecoration: 'none',
          color: 'var(--color-link)',
          fontSize: '0.875rem',
          fontWeight: 600,
          borderRadius: isDropdown ? 0 : '12px',
          border: isDropdown ? 'none' : '1px solid var(--color-border)',
          borderTop: isDropdown && hasResults ? '1px solid var(--color-border)' : 'none',
          transition: 'background-color 0.15s ease',
        }}
        onMouseEnter={event => {
          event.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
        }}
        onMouseLeave={event => {
          event.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span>Show more results for &ldquo;{query}&rdquo;</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ width: '14px', height: '14px', flexShrink: 0 }}
          aria-hidden="true"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </Link>
    );
  };

  const searchOverlay = !isMobile && isSearchOpen && portalTarget
    ? createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search products"
      onClick={handleSearchClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 15, 15, 0.7)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: isMobile
          ? 'max(10px, env(safe-area-inset-top)) 12px max(10px, env(safe-area-inset-bottom))'
          : '60px 12px 60px',
        boxSizing: 'border-box',
        minHeight: '100vh',
        overflow: 'hidden',
      }}
    >
      <div
        onClick={event => event.stopPropagation()}
        style={{
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-text-primary)',
          borderRadius: isMobile ? '16px' : '12px',
          width: '100%',
          maxWidth: isMobile ? '100%' : '640px',
          boxShadow: '0 24px 48px rgba(15, 23, 42, 0.25)',
          border: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          maxHeight: isMobile
            ? 'calc(100vh - max(20px, env(safe-area-inset-top)) - max(12px, env(safe-area-inset-bottom)))'
            : 'calc(100vh - 160px)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: isMobile ? '12px 12px 10px' : '14px 16px 10px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <label
            htmlFor="nav-search-input"
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--color-text-secondary)',
            }}
          >
            Search
          </label>
          <button
            type="button"
            onClick={handleSearchClose}
            aria-label="Close search dialog"
            style={{
              border: 'none',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              borderRadius: '9999px',
              width: '36px',
              height: '36px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease, color 0.2s ease',
              flexShrink: 0,
            }}
            onMouseEnter={event => {
              event.currentTarget.style.backgroundColor =
                'var(--color-surface-muted)';
              event.currentTarget.style.color = 'var(--color-navbar-text)';
            }}
            onMouseLeave={event => {
              event.currentTarget.style.backgroundColor = 'transparent';
              event.currentTarget.style.color = 'var(--color-text-secondary)';
            }}
          >
            <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>×</span>
          </button>
        </div>
        <form
          onSubmit={handleSearchSubmit}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: isMobile ? '10px 12px 8px' : '12px 16px 10px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ position: 'relative', width: '100%' }}>
            <span
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-text-secondary)',
                pointerEvents: 'none',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }} aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              id="nav-search-input"
              ref={searchInputRef}
              type="search"
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              placeholder="Search products..."
              style={{
                width: '100%',
                padding: '10px 10px 10px 40px',
                borderRadius: '12px',
                border: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-surface-muted)',
                color: 'var(--color-text-primary)',
                fontSize: '1rem',
                outline: 'none',
                boxShadow: '0 0 0 2px transparent',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                boxSizing: 'border-box',
              }}
              onFocus={event => {
                event.target.style.boxShadow = '0 0 0 3px var(--brand-accent-overlay)';
                event.target.style.borderColor = 'var(--brand-primary)';
              }}
              onBlur={event => {
                event.target.style.boxShadow = '0 0 0 2px transparent';
                event.target.style.borderColor = 'var(--color-border)';
              }}
            />
          </div>
        </form>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            minHeight: 0,
            flex: 1,
            padding: isMobile
              ? '10px 12px calc(10px + env(safe-area-inset-bottom))'
              : '12px 16px 16px',
          }}
        >
          {hasQuery && (
            <div style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              {showNoResults
                ? `No products found for "${trimmedSearch}".`
                : showResultsSummary &&
                  `Showing ${searchResults.length} matching ${
                    searchResults.length === 1 ? 'item' : 'items'
                  }.`}
            </div>
          )}

          {searchResults.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid var(--color-border)',
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              {searchResults.map(product =>
                renderSearchResultItem({
                  product,
                  onSelect: handleSearchClose,
                  divider: true,
                  key: product.id,
                })
              )}
            </div>
          )}

          {hasQuery &&
            renderShowMoreResultsLink({
              query: trimmedSearch,
              onSelect: handleSearchClose,
              variant: 'dropdown',
              hasResults: searchResults.length > 0,
            })}
        </div>
      </div>
    </div>,
    portalTarget
  ) : null;

  const cartButton = (
    <button
      type="button"
      onClick={() => navigate('/cart')}
      style={cartButtonStyle}
      aria-label="View shopping cart"
      onMouseEnter={() => setIsCartHovered(true)}
      onMouseLeave={() => setIsCartHovered(false)}
      onFocus={() => setIsCartHovered(true)}
      onBlur={() => setIsCartHovered(false)}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={iconSvgStyle}
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="9" cy="21" r="1" />
        <circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
      </svg>
      {cartCount > 0 && (
        <span style={cartBadgeStyle}>{cartCount}</span>
      )}
    </button>
  );

  const sidebarWidth = isMobile ? 280 : 320;
  const currentNavPadding = isMobile ? navPadding.mobile : navPadding.desktop;

  // ─── Modern Desktop Nav ───────────────────────────────────────────────────
  if (showModernDesktop) {
    const showDesktopDropdown = isDesktopSearchFocused && trimmedDesktopSearch.length > 0;

    return (
      <>
        <nav
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: 'var(--color-navbar-surface)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            zIndex: 100,
            borderBottom: '1px solid var(--color-navbar-border)',
          }}
        >
          {/* Row 1: Logo | Search bar | Cart */}
          <div
            style={{
              maxWidth: MAX_CONTENT_WIDTH,
              margin: '0 auto',
              padding: '0 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              height: `${clampedLogoSize + 16}px`,
            }}
          >
            {/* Logo */}
            <Link to="/" style={{ textDecoration: 'none', flexShrink: 0 }}>
              {logoURL ? (
                <img
                  src={logoURL}
                  alt="Store logo"
                  width={Math.min(logoSize, 72)}
                  height={Math.min(logoSize, 72)}
                  fetchPriority="high"
                  decoding="sync"
                  onLoad={() => {
                    if (logoFirstLoad.current) {
                      logoFirstLoad.current = false;
                      setLogoLoaded(true);
                    }
                  }}
                  style={{
                    height: `${Math.min(logoSize, 72)}px`,
                    width: 'auto',
                    maxWidth: '160px',
                    objectFit: 'contain',
                    opacity: logoLoaded ? 1 : 0,
                    transition: 'opacity 0.3s ease-in',
                  }}
                />
              ) : (
                storeTitle && (
                  <span
                    style={{
                      color: 'var(--color-text-primary)',
                      fontSize: '20px',
                      fontFamily: 'var(--font-family-base)',
                      fontWeight: '600',
                      letterSpacing: '-0.02em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {storeTitle}
                  </span>
                )
              )}
            </Link>

            {/* Search bar */}
            <div style={{ flex: 1, position: 'relative' }}>
              <form
                onSubmit={event => {
                  event.preventDefault();
                  if (trimmedDesktopSearch) {
                    navigate(`/search?q=${encodeURIComponent(trimmedDesktopSearch)}`);
                    setDesktopSearchTerm('');
                    setIsDesktopSearchFocused(false);
                  }
                }}
                style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
              >
                <span
                  style={{
                    position: 'absolute',
                    left: '14px',
                    color: 'var(--color-text-secondary)',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }} aria-hidden="true">
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  ref={desktopSearchRef}
                  type="search"
                  value={desktopSearchTerm}
                  onChange={event => setDesktopSearchTerm(event.target.value)}
                  onFocus={() => setIsDesktopSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsDesktopSearchFocused(false), 150)}
                  placeholder="Search products..."
                  style={{
                    width: '100%',
                    padding: '9px 14px 9px 40px',
                    borderRadius: BORDER_RADIUS,
                    border: '1.5px solid var(--color-border)',
                    backgroundColor: 'var(--color-surface-muted)',
                    color: 'var(--color-text-primary)',
                    fontSize: '0.9rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    boxSizing: 'border-box',
                  }}
                  onFocusCapture={event => {
                    event.target.style.borderColor = 'var(--brand-primary)';
                    event.target.style.boxShadow = '0 0 0 3px var(--brand-accent-overlay)';
                  }}
                  onBlurCapture={event => {
                    event.target.style.borderColor = 'var(--color-border)';
                    event.target.style.boxShadow = 'none';
                  }}
                />
              </form>

              {/* Inline search dropdown */}
              {showDesktopDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: BORDER_RADIUS,
                    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
                    zIndex: 200,
                    overflow: 'hidden',
                  }}
                >
                  {desktopSearchResults.length === 0 ? (
                    <div style={{ padding: '12px', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                      No products found for &ldquo;{trimmedDesktopSearch}&rdquo;
                    </div>
                  ) : (
                    desktopSearchResults.map(product =>
                      renderSearchResultItem({
                        product,
                        onSelect: () => {
                          setDesktopSearchTerm('');
                          setIsDesktopSearchFocused(false);
                        },
                        divider: true,
                        key: product.id,
                      })
                    )
                  )}

                  {/* Show more results */}
                  {renderShowMoreResultsLink({
                    query: trimmedDesktopSearch,
                    onSelect: () => {
                      setDesktopSearchTerm('');
                      setIsDesktopSearchFocused(false);
                    },
                    variant: 'dropdown',
                    hasResults: desktopSearchResults.length > 0,
                  })}
                </div>
              )}
            </div>

            {/* Cart button */}
            {showCart && cartButton}
          </div>

          {/* Row 2: Category strip */}
          <div
            style={{
              borderTop: '1px solid var(--color-navbar-border)',
              backgroundColor: 'var(--color-navbar-surface)',
              display: 'block',
            }}
          >
            <div
              style={{
                maxWidth: MAX_CONTENT_WIDTH,
                margin: '0 auto',
                padding: '0 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                height: '36px',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {headerNavItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{
                    textDecoration: 'none',
                    color: isActive(item.path) ? 'var(--color-navbar-pill-text)' : 'var(--color-navbar-text)',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    padding: '4px 12px',
                    borderRadius: BORDER_RADIUS,
                    backgroundColor: isActive(item.path) ? 'var(--color-navbar-pill-bg)' : 'transparent',
                    transition: 'all 0.15s ease',
                    flexShrink: 0,
                  }}
                  onMouseEnter={e => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {item.label}
                </Link>
              ))}

              {sortedCategories.length > 0 && (
                <>
                  <span
                    style={{
                      width: '1px',
                      height: '16px',
                      backgroundColor: 'var(--color-navbar-border)',
                      margin: '0 4px',
                      flexShrink: 0,
                    }}
                  />
                  {sortedCategories.map(category => {
                    const label = category.title || category.name || 'Category';
                    const catPath = `/category/${category.id}`;
                    return (
                      <Link
                        key={category.id}
                        to={catPath}
                        style={{
                          textDecoration: 'none',
                          color: isActive(catPath) ? 'var(--color-navbar-pill-text)' : 'var(--color-navbar-text)',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          padding: '4px 12px',
                          borderRadius: BORDER_RADIUS,
                          backgroundColor: isActive(catPath) ? 'var(--color-navbar-pill-bg)' : 'transparent',
                          transition: 'all 0.15s ease',
                          flexShrink: 0,
                        }}
                        onMouseEnter={e => {
                          if (!isActive(catPath)) {
                            e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
                          }
                        }}
                        onMouseLeave={e => {
                          if (!isActive(catPath)) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {label}
                      </Link>
                    );
                  })}
                  {hasUncategorized && (
                    <Link
                      to="/category/more"
                      style={{
                        textDecoration: 'none',
                        color: isActive('/category/more') ? 'var(--color-navbar-pill-text)' : 'var(--color-navbar-text)',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        padding: '4px 12px',
                        borderRadius: BORDER_RADIUS,
                        backgroundColor: isActive('/category/more') ? 'var(--color-navbar-pill-bg)' : 'transparent',
                        transition: 'all 0.15s ease',
                        flexShrink: 0,
                      }}
                      onMouseEnter={e => {
                        if (!isActive('/category/more')) {
                          e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
                        }
                      }}
                      onMouseLeave={e => {
                        if (!isActive('/category/more')) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      More
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        </nav>

        <style jsx>{`
          div::-webkit-scrollbar { display: none; }
        `}</style>
        {searchOverlay}
      </>
    );
  }

  // ─── Classic Nav ─────────────────────────────────────────────────────────
  return (
    <>
      <nav
        style={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: 'var(--color-navbar-surface)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          zIndex: 100,
          borderBottom: '1px solid var(--color-navbar-border)',
        }}
      >
        <div
          className="nav-safe-area"
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `${currentNavPadding}px 16px`,
            minHeight: logoURL ? `${clampedLogoSize}px` : undefined,
            maxWidth: MAX_CONTENT_WIDTH,
            margin: '0 auto',
            width: '100%',
            transition: 'padding 0.3s ease-in-out',
          }}
        >
          {/* Hamburger Menu Button - Left */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={menuButtonStyle}
            aria-label="Toggle menu"
            onMouseEnter={() => setIsMenuHovered(true)}
            onMouseLeave={() => setIsMenuHovered(false)}
            onFocus={() => setIsMenuHovered(true)}
            onBlur={() => setIsMenuHovered(false)}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
                width: '18px',
                height: '18px',
              }}
            >
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: 'currentColor',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  transform: isMenuOpen
                    ? 'rotate(45deg) translate(4px, 4px)'
                    : 'none',
                }}
              />
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: 'currentColor',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  opacity: isMenuOpen ? 0 : 1,
                }}
              />
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: 'currentColor',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  transform: isMenuOpen
                    ? 'rotate(-45deg) translate(5px, -5px)'
                    : 'none',
                }}
              />
            </div>
          </button>

          {/* Logo - Center */}
          <Link
            to="/"
            style={{
              ...brandLinkStyle,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            {logoURL ? (
              <img
                src={logoURL}
                alt="Koral Krush logo"
                width={clampedLogoSize}
                height={clampedLogoSize}
                fetchPriority="high"
                decoding="sync"
                onLoad={() => {
                  if (logoFirstLoad.current) {
                    logoFirstLoad.current = false;
                    setLogoLoaded(true);
                  }
                }}
                style={{
                  ...logoStyle,
                  opacity: logoLoaded ? 1 : 0,
                  transition: `opacity 0.3s ease-in, ${logoStyle.transition}`,
                }}
              />
            ) : (
              storeTitle && (
                <span style={storeTitleStyle}>{storeTitle}</span>
              )
            )}
          </Link>

          {/* Icon Buttons - Right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {showCart && cartButton}

            <button
              type="button"
              onClick={() => {
                if (isMobile && isSearchOpen) {
                  handleSearchClose();
                } else {
                  handleSearchOpen();
                }
              }}
              style={searchButtonStyle}
              aria-label={isMobile && isSearchOpen ? 'Close product search' : 'Open product search'}
              onMouseEnter={() => setIsSearchHovered(true)}
              onMouseLeave={() => setIsSearchHovered(false)}
              onFocus={() => setIsSearchHovered(true)}
              onBlur={() => setIsSearchHovered(false)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={iconSvgStyle}
                aria-hidden="true"
                focusable="false"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
          </div>
        </div>

        {isMobile && isSearchOpen && (
          <div
            className="nav-safe-area"
            style={{
              maxWidth: MAX_CONTENT_WIDTH,
              margin: '0 auto',
              width: '100%',
              padding: '0 12px 10px',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ position: 'relative' }}>
              <form
                onSubmit={handleSearchSubmit}
                style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
              >
                <span
                  style={{
                    position: 'absolute',
                    left: '14px',
                    color: 'var(--color-text-secondary)',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }} aria-hidden="true">
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  id="nav-search-input-inline"
                  ref={searchInputRef}
                  type="search"
                  value={searchTerm}
                  onChange={event => setSearchTerm(event.target.value)}
                  onFocus={() => setIsInlineSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsInlineSearchFocused(false), 150)}
                  placeholder="Search products..."
                  style={{
                    width: '100%',
                    padding: '9px 14px 9px 40px',
                    borderRadius: BORDER_RADIUS,
                    border: '1.5px solid var(--color-border)',
                    backgroundColor: 'var(--color-surface-muted)',
                    color: 'var(--color-text-primary)',
                    fontSize: '0.9rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    boxSizing: 'border-box',
                  }}
                  onFocusCapture={event => {
                    event.target.style.borderColor = 'var(--brand-primary)';
                    event.target.style.boxShadow = '0 0 0 3px var(--brand-accent-overlay)';
                  }}
                  onBlurCapture={event => {
                    event.target.style.borderColor = 'var(--color-border)';
                    event.target.style.boxShadow = 'none';
                  }}
                />
              </form>

              {isInlineSearchFocused && trimmedSearch.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: BORDER_RADIUS,
                    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
                    zIndex: 220,
                    overflow: 'hidden',
                    maxHeight: 'min(52vh, 360px)',
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {searchResults.length === 0 ? (
                    <div style={{ padding: '12px', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                      No products found for &ldquo;{trimmedSearch}&rdquo;
                    </div>
                  ) : (
                    searchResults.map(product =>
                      renderSearchResultItem({
                        product,
                        onSelect: handleSearchClose,
                        divider: true,
                        key: `inline-${product.id}`,
                      })
                    )
                  )}

                  {renderShowMoreResultsLink({
                    query: trimmedSearch,
                    onSelect: handleSearchClose,
                    variant: 'dropdown',
                    hasResults: searchResults.length > 0,
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Menu Overlay */}
      {isMenuOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99,
            animation: 'fadeIn 0.2s ease',
          }}
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Sidebar Menu Panel */}
      <div
        className="sidebar-safe-area"
        style={{
          position: 'fixed',
          top: 0,
          left: isMenuOpen ? 0 : `-${sidebarWidth}px`,
          width: `${sidebarWidth}px`,
          height: '100vh',
          backgroundColor: 'var(--color-mobile-menu-surface)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          zIndex: 101,
          transition: 'left 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: `${clampedLogoSize + 20}px`,
        }}
      >
        <div
          style={{
            padding: '12px',
            overflowY: 'auto',
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {sidebarMenuContent}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .sidebar-link:hover {
          background-color: rgba(128, 128, 128, 0.15) !important;
          border-radius: 8px !important;
        }
      `}</style>
      {searchOverlay}
    </>
  );
}

export default NavigationBar;
